const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL || 'http://localhost:3005/api/v1';

// ============== Types ==============

// Raw API response may use different field naming conventions
export interface PaymentMethodRaw {
  id: string;
  stripe_payment_method_id?: string;
  stripePaymentMethodId?: string;
  payment_type?: string;
  paymentType?: string;
  type?: string;
  // Card last 4 digits - multiple naming conventions
  last_4?: string;
  last4?: string;
  card_last4?: string;
  cardLast4?: string;
  // Card brand - multiple naming conventions
  brand?: string;
  card_brand?: string;
  cardBrand?: string;
  // Expiry month - multiple naming conventions
  expiry_month?: number;
  expiryMonth?: number;
  exp_month?: number;
  expMonth?: number;
  card_exp_month?: number;
  cardExpMonth?: number;
  // Expiry year - multiple naming conventions
  expiry_year?: number;
  expiryYear?: number;
  exp_year?: number;
  expYear?: number;
  card_exp_year?: number;
  cardExpYear?: number;
  // Default flag
  is_default?: boolean;
  isDefault?: boolean;
  // Timestamps
  created_at?: string;
  createdAt?: string;
}

// Normalized interface for frontend use
export interface PaymentMethod {
  id: string;
  stripe_payment_method_id: string;
  payment_type: string;
  last_4: string;
  brand: string;
  expiry_month: number;
  expiry_year: number;
  is_default: boolean;
  created_at: string;
}

// Normalize API response to consistent format
function normalizePaymentMethod(raw: PaymentMethodRaw): PaymentMethod {
  return {
    id: raw.id,
    stripe_payment_method_id: raw.stripe_payment_method_id || raw.stripePaymentMethodId || '',
    payment_type: raw.payment_type || raw.paymentType || raw.type || 'card',
    last_4: raw.last_4 || raw.last4 || raw.card_last4 || raw.cardLast4 || '',
    brand: raw.brand || raw.card_brand || raw.cardBrand || '',
    expiry_month: raw.expiry_month || raw.expiryMonth || raw.exp_month || raw.expMonth || raw.card_exp_month || raw.cardExpMonth || 0,
    expiry_year: raw.expiry_year || raw.expiryYear || raw.exp_year || raw.expYear || raw.card_exp_year || raw.cardExpYear || 0,
    is_default: raw.is_default ?? raw.isDefault ?? false,
    created_at: raw.created_at || raw.createdAt || '',
  };
}

export interface AddPaymentMethodRequest {
  paymentMethodId: string;
  setAsDefault?: boolean;
}

export interface AddPaymentMethodResponse {
  success: boolean;
  data?: {
    paymentMethodId: string;
    type: string;
    card: {
      brand: string;
      last4: string;
      expMonth: number;
      expYear: number;
    };
    isDefault: boolean;
  };
  error?: string;
  message?: string;
}

export interface PaymentMethodsResponse {
  success: boolean;
  data: PaymentMethod[];
  error?: string;
  message?: string;
}

interface PaymentMethodsRawResponse {
  success: boolean;
  data: PaymentMethodRaw[];
  error?: string;
  message?: string;
}

export interface SetDefaultResponse {
  success: boolean;
  data?: {
    paymentMethodId: string;
    isDefault: boolean;
  };
  error?: string;
  message?: string;
}

export interface DeletePaymentMethodResponse {
  success: boolean;
  data?: {
    paymentMethodId: string;
    deleted: boolean;
  };
  error?: string;
  message?: string;
}

// ============== Service ==============

class PaymentMethodService {
  /**
   * Get all payment methods for the organization
   * GET /payment-methods
   */
  async getPaymentMethods(): Promise<PaymentMethodsResponse> {
    const response = await fetch(`${API_BASE_URL}/payment-methods`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    const rawResponse: PaymentMethodsRawResponse = await response.json();

    // Normalize the response data to handle both snake_case and camelCase
    return {
      success: rawResponse.success,
      data: rawResponse.data ? rawResponse.data.map(normalizePaymentMethod) : [],
      error: rawResponse.error,
      message: rawResponse.message,
    };
  }

  /**
   * Add a new payment method
   * POST /payment-methods
   * Note: paymentMethodId is obtained from Stripe Elements
   */
  async addPaymentMethod(request: AddPaymentMethodRequest): Promise<AddPaymentMethodResponse> {
    const response = await fetch(`${API_BASE_URL}/payment-methods`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    return await response.json();
  }

  /**
   * Set a payment method as default
   * PUT /payment-methods/:id/default
   * @param stripePaymentMethodId - The Stripe payment method ID (e.g., pm_xxx)
   */
  async setDefaultPaymentMethod(stripePaymentMethodId: string): Promise<SetDefaultResponse> {
    const response = await fetch(`${API_BASE_URL}/payment-methods/${stripePaymentMethodId}/default`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    return await response.json();
  }

  /**
   * Remove a payment method
   * DELETE /payment-methods/:id
   * @param stripePaymentMethodId - The Stripe payment method ID (e.g., pm_xxx)
   */
  async deletePaymentMethod(stripePaymentMethodId: string): Promise<DeletePaymentMethodResponse> {
    const response = await fetch(`${API_BASE_URL}/payment-methods/${stripePaymentMethodId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    return await response.json();
  }
}

export const paymentMethodService = new PaymentMethodService();
